/**
 * DefaultPricingTiersPanel — the DEFAULT graduated volume-band table, edited on Pricing
 * Management alongside the flat per-service prices it sits above.
 *
 * These bands apply to any client switched on for tiered pricing who has no table of their
 * own. A client with their own table (Client Edit → Volume Tier Pricing) ignores these.
 *
 * Treat a save here as repricing the whole book. Volume tiers are on by default for every
 * client since the 20260810 migration, so the set this table reaches is "everyone except the
 * clients holding a negotiated arrangement" — not the handful someone opted in, which is what
 * it meant when the switch first shipped.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layers, Loader2, RefreshCw, Save } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  listDefaultPricingTiers, saveDefaultPricingTiers,
  toBandRows, toBandItems, validateBandRows, BandRow,
} from '../../services/pricingTiersService';
import PricingTierBandEditor from './PricingTierBandEditor';

// NMVTIS is the only tiered service today. MVR is deliberately not tiered; if Theft/NVS is
// ever given bands, add it here and this panel gains a service selector.
const SERVICE_TYPE = 'NMVTIS';

const DefaultPricingTiersPanel: React.FC = () => {
  const [rows, setRows] = useState<BandRow[]>([]);
  const [savedRows, setSavedRows] = useState<BandRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const tiers = await listDefaultPricingTiers(SERVICE_TYPE);
      const asRows = toBandRows(tiers);
      setRows(asRows);
      setSavedRows(asRows);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(savedRows),
    [rows, savedRows],
  );

  const handleSave = async () => {
    const validationError = validateBandRows(rows);
    if (validationError) { setError(validationError); setSuccess(''); return; }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await saveDefaultPricingTiers(SERVICE_TYPE, toBandItems(rows));
      const asRows = toBandRows(updated);
      setRows(asRows);
      setSavedRows(asRows);
      setSuccess('Default tier table saved.');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card overflow-hidden mt-6">
      <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#c41e3a]" /> Default {SERVICE_TYPE} Volume Tiers
        </h2>
        <button onClick={load} className="text-xs text-[#64748b] hover:text-[#c41e3a] inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="px-6 py-5">
        <p className="text-xs text-[#64748b] mb-4">
          These are standard rates. Volume tiers are on by default for every client, so saving here
          changes what most clients pay on their next lookup. Clients with their own table, or with a
          negotiated per-service price or discount, are not affected — theirs is set on their edit
          page, under Volume Tier Pricing.
        </p>

        {error && <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}
        {success && <div className="mb-4 p-3 rounded-lg border bg-emerald-50 border-emerald-200 text-sm text-emerald-800">{success}</div>}

        {loading ? (
          <div className="py-8 text-center text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading tiers…</div>
        ) : (
          <PricingTierBandEditor rows={rows} onChange={setRows} disabled={saving} />
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSave} disabled={!dirty || saving || loading}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save tiers
          </button>
        </div>
      </div>
    </div>
  );
};

export default DefaultPricingTiersPanel;
